import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  Tooltip,
  Button,
  Slider,
} from '@mui/material';
import { ExpandMore, Settings, PlayArrow, Equalizer } from '@mui/icons-material';
import { ProcessingConfig, Genre, FrequencyPreset, FrequencyBandWeights } from '../types';
import { getAvailableGenres, applyGenrePreset } from '../utils/genrePresets';
import { getAvailableFrequencyPresets, applyFrequencyPreset, DEFAULT_FREQUENCY_WEIGHTS } from '../utils/frequencyPresets';
import PresetManagerUI from './PresetManagerUI';

interface ProcessingConfigProps {
  config: ProcessingConfig;
  onChange: (config: ProcessingConfig) => void;
  disabled?: boolean;
  onStart?: () => void;
  canStart?: boolean;
}

const ProcessingConfigComponent: React.FC<ProcessingConfigProps> = ({
  config,
  onChange,
  disabled = false,
  onStart,
  canStart = false,
}) => {
  const genres = getAvailableGenres();
  const frequencyPresets = getAvailableFrequencyPresets();
  const currentGenre = config.genre || 'auto';
  const currentFrequencyPreset = config.frequencyPreset || 'balanced';
  const [showFrequencySliders, setShowFrequencySliders] = useState(false);

  const handleChange = (field: keyof ProcessingConfig, value: any) => {
    onChange({
      ...config,
      [field]: value,
    });
  };

  const handleGenreChange = (genre: Genre) => {
    // Apply genre preset
    const presetConfig = applyGenrePreset(genre, config);
    onChange({
      ...presetConfig,
      genre,
    });
  };

  const handleFrequencyPresetChange = (preset: FrequencyPreset) => {
    const weights = applyFrequencyPreset(preset, config.frequencyWeights);
    onChange({
      ...config,
      frequencyPreset: preset,
      frequencyWeights: weights,
    });
    // Always show sliders except for balanced preset
    setShowFrequencySliders(preset !== 'balanced');
  };

  const handleFrequencyWeightChange = (band: keyof FrequencyBandWeights, value: number) => {
    const newWeights = {
      ...(config.frequencyWeights || DEFAULT_FREQUENCY_WEIGHTS),
      [band]: value,
    };
    onChange({
      ...config,
      frequencyWeights: newWeights,
      frequencyPreset: 'custom', // Switch to custom when manually adjusting
    });
  };

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Processing Settings
      </Typography>

      {/* Preset Manager */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Presets
        </Typography>
        <PresetManagerUI
          currentConfig={config}
          onLoadPreset={(loadedConfig) => onChange(loadedConfig)}
        />
      </Box>

      {/* ==================== PRE-PROCESSING ==================== */}
      <Box sx={{
        mb: 3,
        p: 2.5,
        border: '2px solid',
        borderColor: 'primary.light',
        borderRadius: 2,
        bgcolor: 'rgba(25, 118, 210, 0.08)'
      }}>
        <Typography variant="h6" gutterBottom sx={{
          fontWeight: 600,
          color: 'primary.dark',
          mb: 2
        }}>
          Pre-Processing
        </Typography>

        {/* De-esser Control */}
        <Box sx={{
          p: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          mb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              De-esser
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={config.useDeesser === true}
                  onChange={(e) => handleChange('useDeesser', e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label="Enable"
              labelPlacement="start"
            />
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            Reduces harsh high-frequency content (4-10kHz) in the full mix before mastering. Helps control listener fatigue and excessive brightness in the overall mix.
          </Typography>

          {config.useDeesser && (
            <>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  <Chip
                    label={`${config.deesserAmount || 50}%`}
                    size="small"
                    color="primary"
                    sx={{ minWidth: '60px', fontWeight: 'bold' }}
                  />
                </Box>
                <Slider
                  value={config.deesserAmount || 50}
                  onChange={(_, value) => handleChange('deesserAmount', value)}
                  disabled={disabled}
                  min={0}
                  max={100}
                  step={5}
                  marks={[
                    { value: 0, label: 'Off' },
                    { value: 50, label: 'Moderate' },
                    { value: 100, label: 'Heavy' },
                  ]}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Target Frequency</Typography>
                  <Chip
                    label={`${(config.deesserFrequency || 7000) / 1000}kHz`}
                    size="small"
                    color="primary"
                    sx={{ minWidth: '60px', fontWeight: 'bold' }}
                  />
                </Box>
                <Slider
                  value={config.deesserFrequency || 7000}
                  onChange={(_, value) => handleChange('deesserFrequency', value)}
                  disabled={disabled}
                  min={4000}
                  max={10000}
                  step={500}
                  marks={[
                    { value: 4000, label: '4k' },
                    { value: 7000, label: '7k' },
                    { value: 10000, label: '10k' },
                  ]}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                  4-6kHz: Male voices • 6-8kHz: Female voices • 8-10kHz: Very bright sources
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Frequency EQ Control */}
        <Box sx={{
          p: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          mb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Equalizer />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Frequency EQ
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={config.useFrequencyWeighting === true}
                  onChange={(e) => handleChange('useFrequencyWeighting', e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label="Enable"
              labelPlacement="start"
            />
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            <strong>OPTIONAL:</strong> Not needed for the core mastering process. Use only if you want to shape the frequency balance of your target audio before mastering. Try the main process first without this enabled.
          </Typography>

          {/* Frequency Preset Selector */}
          {config.useFrequencyWeighting === true && (
            <>
              <FormControl fullWidth disabled={disabled} sx={{ mb: 2 }}>
            <InputLabel>EQ Profile</InputLabel>
            <Select
              value={currentFrequencyPreset}
              label="EQ Profile"
              onChange={(e) => handleFrequencyPresetChange(e.target.value as FrequencyPreset)}
            >
              {frequencyPresets.map((preset) => (
                <MenuItem key={preset.value} value={preset.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box sx={{
                      minWidth: '60px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      color: 'primary.main',
                      textAlign: 'center'
                    }}>
                      {preset.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1">{preset.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {preset.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
              <MenuItem value="custom">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{
                    minWidth: '60px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    textAlign: 'center'
                  }}>
                    CUSTOM
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1">Custom</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Manual control over all frequency bands
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Show current preset description */}
          {currentFrequencyPreset !== 'custom' && currentFrequencyPreset !== 'balanced' && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Active EQ:</strong>{' '}
                {currentFrequencyPreset === 'warm' && 'Reduces harsh high-mids (2-6kHz) by blending with original'}
                {currentFrequencyPreset === 'bright' && 'Enhances highs and presence by boosting filtered bands'}
                {currentFrequencyPreset === 'vocal-focus' && 'Reduces sibilance while maintaining vocal clarity'}
                {currentFrequencyPreset === 'bass-heavy' && 'Boosts low-end, reduces high-mids'}
                {' '}- Adjust sliders below to fine-tune
              </Typography>
            </Box>
          )}

          {currentFrequencyPreset === 'balanced' && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Balanced Mode:</strong> All frequencies at 100%. Select a different profile or "Custom" to adjust individual bands.
              </Typography>
            </Box>
          )}

          {/* Advanced: Show frequency sliders for all presets except balanced */}
          {currentFrequencyPreset !== 'balanced' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Adjust EQ per band (100% = no change, &lt;100% = blend with original, &gt;100% = enhance)
              </Typography>
              <Box sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'center' },
                alignItems: 'flex-end',
                gap: { xs: 1, sm: 1.5 },
                minHeight: '200px',
                overflowX: 'auto',
                overflowY: 'hidden',
                px: { xs: 1, sm: 2 },
                mx: { xs: -1, sm: 0 },
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'divider',
                  borderRadius: 1,
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'primary.main',
                  borderRadius: 1,
                },
              }}>
                {[
                  { key: 'subBass', label: 'Sub', range: '20-60', color: '#8B4513' },
                  { key: 'bass', label: 'Bass', range: '60-250', color: '#A0522D' },
                  { key: 'lowMids', label: 'Low-Mid', range: '250-500', color: '#CD853F' },
                  { key: 'mids', label: 'Mids', range: '500-2k', color: '#DAA520' },
                  { key: 'highMids', label: 'Hi-Mid', range: '2-6k', color: '#FF6347' },
                  { key: 'presence', label: 'Pres', range: '6-12k', color: '#FF8C00' },
                  { key: 'air', label: 'Air', range: '12-20k', color: '#FFD700' },
                ].map((band) => {
                  const value = config.frequencyWeights?.[band.key as keyof FrequencyBandWeights] || 100;
                  // Convert percentage to dB: 0% = -∞, 100% = 0dB, 200% = +6dB
                  const dbValue = value === 0 ? '-∞' : (20 * Math.log10(value / 100)).toFixed(1);
                  const dbLabel = value === 0 ? '-∞' : `${Number(dbValue) > 0 ? '+' : ''}${dbValue}`;
                  return (
                    <Box key={band.key} sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: { xs: '60px', sm: '70px' },
                      flex: { xs: '0 0 auto', md: '1 1 auto' }
                    }}>
                      <Chip
                        label={`${dbLabel}dB`}
                        size="small"
                        sx={{
                          bgcolor: value < 80 ? 'success.light' : value > 120 ? 'warning.light' : 'default',
                          height: { xs: '22px', sm: '24px' },
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          mb: 1,
                          minWidth: { xs: '55px', sm: '60px' },
                          fontWeight: 600
                        }}
                      />
                      <Slider
                        orientation="vertical"
                        value={value}
                        onChange={(_, newValue) => handleFrequencyWeightChange(band.key as keyof FrequencyBandWeights, newValue as number)}
                        disabled={disabled}
                        min={0}
                        max={200}
                        step={2}
                        marks={[
                          { value: 0, label: '' },
                          { value: 100, label: '' },
                          { value: 200, label: '' },
                        ]}
                        sx={{
                          height: { xs: 120, sm: 150 },
                          '& .MuiSlider-thumb': {
                            bgcolor: band.color,
                            width: { xs: 12, sm: 16 },
                            height: { xs: 12, sm: 16 },
                          },
                          '& .MuiSlider-track': {
                            bgcolor: band.color,
                          },
                        }}
                      />
                      <Typography variant="caption" fontWeight="bold" sx={{
                        mt: 1,
                        textAlign: 'center',
                        fontSize: { xs: '0.6rem', sm: '0.75rem' },
                        lineHeight: 1.2
                      }}>
                        {band.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: { xs: '0.55rem', sm: '0.65rem' },
                        textAlign: 'center',
                        lineHeight: 1.2
                      }}>
                        {band.range}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  0%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  100%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  200%
                </Typography>
              </Box>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: 'center' }}>
            Tip: Use "Warm" to reduce harshness - "Bright" for sparkle - "Custom" for full control
          </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* ==================== REFERENCE MATCHING (MAIN PROCESS) ==================== */}
      <Box sx={{
        mb: 3,
        p: 2.5,
        border: '2px solid',
        borderColor: 'success.light',
        borderRadius: 2,
        bgcolor: 'rgba(46, 125, 50, 0.08)'
      }}>
        <Typography variant="h6" gutterBottom sx={{
          fontWeight: 600,
          color: 'success.dark',
          mb: 2
        }}>
          Reference Matching (Main Process)
        </Typography>

      {/* Genre Preset Selector */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5 }}>
          Genre Preset
        </Typography>
        <FormControl fullWidth disabled={disabled}>
          <InputLabel>Select Genre</InputLabel>
          <Select
            value={currentGenre}
            label="Select Genre"
            onChange={(e) => handleGenreChange(e.target.value as Genre)}
          >
            {genres.map((genre) => (
              <MenuItem key={genre.value} value={genre.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{
                    minWidth: '60px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    textAlign: 'center'
                  }}>
                    {genre.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1">{genre.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {genre.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {currentGenre !== 'auto' && currentGenre !== 'custom' && (
          <Box sx={{ mt: 1.5 }}>
            <Chip
              label={`${genres.find(g => g.value === currentGenre)?.label} Preset Active`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Basic Settings */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2
        }}>
          <FormControl fullWidth disabled={disabled}>
            <InputLabel>Output Format</InputLabel>
            <Select
              value={config.outputFormat}
              label="Output Format"
              onChange={(e) => handleChange('outputFormat', e.target.value)}
            >
              <MenuItem value="pcm16">16-bit PCM (CD Quality)</MenuItem>
              <MenuItem value="pcm24">24-bit PCM (Studio Quality)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Output Filename (optional)"
            value={config.outputFilename || ''}
            onChange={(e) => handleChange('outputFilename', e.target.value)}
            disabled={disabled}
            placeholder="Auto-generated if empty"
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Advanced Settings */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings />
            <Typography variant="subtitle1">
              Advanced Settings {currentGenre !== 'custom' && '(Override Preset)'}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {currentGenre !== 'custom' && (
              <Typography variant="caption" color="warning.main" sx={{ mb: 1 }}>
                WARNING: Changing these values will override the genre preset
              </Typography>
            )}

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2
            }}>
              <TextField
                fullWidth
                label="Max Length (minutes)"
                type="number"
                value={config.maxLength ? config.maxLength / 60 : 15}
                onChange={(e) => handleChange('maxLength', parseFloat(e.target.value) * 60)}
                disabled={disabled}
                inputProps={{ min: 1, max: 60, step: 0.5 }}
                helperText="Maximum track length to process"
              />
              <FormControl fullWidth disabled={disabled}>
                <InputLabel>Sample Rate</InputLabel>
                <Select
                  value={config.internalSampleRate || 48000}
                  label="Sample Rate"
                  onChange={(e) => handleChange('internalSampleRate', e.target.value)}
                >
                  <MenuItem value={44100}>44.1 kHz (CD Quality)</MenuItem>
                  <MenuItem value={48000}>48 kHz (Professional)</MenuItem>
                  <MenuItem value={96000}>96 kHz (High Resolution)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2
            }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  EQ Smoothing (LOWESS)
                </Typography>
                <Box sx={{ px: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <Chip
                      label={`${(config.lowessFrac || 0.05).toFixed(2)}`}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 'bold', minWidth: '70px' }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: '50px', fontSize: '0.7rem' }}>
                      Sharp
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <input
                        type="range"
                        min="0.01"
                        max="0.15"
                        step="0.01"
                        value={config.lowessFrac || 0.05}
                        onChange={(e) => handleChange('lowessFrac', parseFloat(e.target.value))}
                        disabled={disabled}
                        style={{ width: '100%' }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: '50px', textAlign: 'right', fontSize: '0.7rem' }}>
                      Smooth
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                  Controls EQ curve smoothness
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="Preview Length (seconds)"
                type="number"
                value={config.previewLength || 30}
                onChange={(e) => handleChange('previewLength', parseInt(e.target.value))}
                disabled={disabled}
                inputProps={{ min: 5, max: 120, step: 5 }}
                helperText="Length of preview clips"
              />
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2
            }}>
              <FormControl fullWidth disabled={disabled}>
                <InputLabel>FFT Size</InputLabel>
                <Select
                  value={config.fftSize || 4096}
                  label="FFT Size"
                  onChange={(e) => handleChange('fftSize', e.target.value)}
                >
                  <MenuItem value={2048}>2048 (Fast)</MenuItem>
                  <MenuItem value={4096}>4096 (Balanced)</MenuItem>
                  <MenuItem value={8192}>8192 (Detailed)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Temporary Folder"
                value={config.tempFolder || ''}
                onChange={(e) => handleChange('tempFolder', e.target.value)}
                disabled={disabled}
                placeholder="Use system default"
                helperText="Custom temporary folder for processing"
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 2 }} />

      {/* Output and Limiter Controls - Side by Side */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 2,
        mb: 2
      }}>
        {/* Output Control */}
        <Box sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" fontWeight="bold">Output Modes</Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    • Limiter ON + Normalize ON: Full mastering (loudest)
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Limiter OFF + Normalize ON: Transparent with clipping protection
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Limiter OFF + Normalize OFF: Raw EQ matching (for external limiting)
                  </Typography>
                </Box>
              }
              arrow
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, cursor: 'help' }}>
                Output Level
              </Typography>
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  checked={config.normalize !== false}
                  onChange={(e) => handleChange('normalize', e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label="Normalize"
              labelPlacement="start"
            />
          </Box>

          <Box sx={{ px: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Chip
                label={`${(config.outputGainDb ?? 0) > 0 ? '+' : ''}${(config.outputGainDb ?? 0).toFixed(1)} dB`}
                color="primary"
                size="small"
                sx={{ fontWeight: 'bold', minWidth: '80px' }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: '50px', fontSize: '0.7rem' }}>
                Quieter
              </Typography>
              <Box sx={{ flex: 1 }}>
                <input
                  type="range"
                  min="-12"
                  max="12"
                  step="0.1"
                  value={config.outputGainDb || 0}
                  onChange={(e) => handleChange('outputGainDb', parseFloat(e.target.value))}
                  disabled={disabled}
                  style={{ width: '100%' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: '50px', textAlign: 'right', fontSize: '0.7rem' }}>
                Louder
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Limiter Control */}
        <Box sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" fontWeight="bold">Internal Limiter Control</Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    • ON: Full mastering (EQ + Limiting)
                  </Typography>
                  <Typography variant="caption" display="block">
                    • OFF: EQ matching only
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    Turn OFF to apply your own hardware limiting after EQ matching
                  </Typography>
                </Box>
              }
              arrow
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, cursor: 'help' }}>
                Internal Limiter
              </Typography>
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  checked={config.useLimiter !== false}
                  onChange={(e) => handleChange('useLimiter', e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label="Enable"
              labelPlacement="start"
            />
          </Box>

          {config.useLimiter !== false && (
            <Box sx={{ px: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <Chip
                  label={`${((config.threshold || 0.95) * 100).toFixed(0)}%`}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 'bold', minWidth: '70px' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: '65px', fontSize: '0.7rem' }}>
                  Dynamics
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <input
                    type="range"
                    min="0.5"
                    max="0.99"
                    step="0.01"
                    value={config.threshold || 0.95}
                    onChange={(e) => handleChange('threshold', parseFloat(e.target.value))}
                    disabled={disabled}
                    style={{ width: '100%' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: '65px', textAlign: 'right', fontSize: '0.7rem' }}>
                  Loudness
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Close Reference Matching section */}
      </Box>

      {/* ==================== POST-PROCESSING ==================== */}
      <Box sx={{
        mb: 3,
        p: 2.5,
        border: '2px solid',
        borderColor: 'warning.light',
        borderRadius: 2,
        bgcolor: 'rgba(237, 108, 2, 0.08)'
      }}>
        <Typography variant="h6" gutterBottom sx={{
          fontWeight: 600,
          color: 'warning.dark',
          mb: 2
        }}>
          Post-Processing
        </Typography>

      {/* Transient Preservation Control */}
      <Box sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        mb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" fontWeight="bold">How Transient Preservation Works</Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Blends original drum hits and vocal attacks back into the mastered output to preserve punch.
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  • 0% = Fully mastered (smoothest)
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
                  • 10-20% = Subtle punch (recommended)
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
                  • 30% = Maximum recommended
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'warning.light' }}>
                  ⚠️ Higher values blend more unmastered audio, which can reduce mastering effectiveness.
                </Typography>
              </Box>
            }
            arrow
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, cursor: 'help' }}>
              Transient Preservation
            </Typography>
          </Tooltip>
          <Chip
            label="Use Sparingly"
            size="small"
            color="warning"
            variant="outlined"
          />
        </Box>

        <Box sx={{ px: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <Chip
              label={`${config.transientPreservation ?? 0}%`}
              color="primary"
              size="small"
              sx={{ fontWeight: 'bold', minWidth: '70px' }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: '60px', fontSize: '0.7rem' }}>
              Smooth
            </Typography>
            <Box sx={{ flex: 1 }}>
              <input
                type="range"
                min="0"
                max="30"
                step="1"
                value={config.transientPreservation ?? 0}
                onChange={(e) => handleChange('transientPreservation', parseFloat(e.target.value))}
                disabled={disabled}
                style={{ width: '100%' }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: '60px', textAlign: 'right', fontSize: '0.7rem' }}>
              Punchy
            </Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
          Blends original dynamics with mastered output. Start at 0% (off) for best mastering results. Use 10-20% only if you need to preserve specific transients.
        </Typography>
      </Box>

      {/* Close Post-Processing section */}
      </Box>

      {/* Analysis Options */}
      <Box sx={{
        mb: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'info.light',
        borderRadius: 1,
        bgcolor: 'rgba(2, 136, 209, 0.05)'
      }}>
        <FormControlLabel
          control={
            <Switch
              checked={config.includeAnalysis === true}
              onChange={(e) => handleChange('includeAnalysis', e.target.checked)}
              disabled={disabled}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Include Detailed Analysis
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Generate LUFS loudness metrics and frequency spectrum charts (+~2 minutes processing time)
              </Typography>
            </Box>
          }
        />
      </Box>

      {/* Dithering Toggle */}
      <Box sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'success.light',
        borderRadius: 1,
        bgcolor: 'rgba(46, 125, 50, 0.05)'
      }}>
        <FormControlLabel
          control={
            <Switch
              checked={config.useDithering !== false}
              onChange={(e) => handleChange('useDithering', e.target.checked)}
              disabled={disabled}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Apply Dithering
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Add TPDF dithering before bit depth conversion - reduces quantization distortion and preserves low-level detail (recommended)
              </Typography>
            </Box>
          }
        />
      </Box>

      {/* Start Processing Button */}
      {onStart && (
        <>
          <Divider sx={{ my: 2 }} />

          {/* Processing Time Estimate */}
          <Box sx={{
            mb: 2,
            p: 1.5,
            bgcolor: 'info.lighter',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'info.light'
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              ⏱️ <strong>Estimated processing time:</strong> {config.includeAnalysis ? '6-7 minutes' : '4-5 minutes'} for typical songs
              {config.includeAnalysis && ' (includes detailed analysis)'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrow />}
              onClick={onStart}
              disabled={!canStart || disabled}
              sx={{ px: 6, py: 1.5 }}
            >
              Start Matching & Mastering
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProcessingConfigComponent;